import type { ReactElement, ReactNode } from 'react';

import { classNames } from '@m1work/shared-utils';

import { Text } from '../Text/Text';

import styles from './FormGroup.module.scss';

export interface FormGroupProps {
    /** Label above the field. */
    label?: string;
    /** `id` of the field the label belongs to. */
    labelFor?: string;
    /** Error message under the field; replaces `helperText`. */
    errorText?: string;
    /** Hint under the field. */
    helperText?: string;
    /** Mark the label with a red asterisk. @default false */
    required?: boolean;
    className?: string;
    children: ReactNode;
}

/**
 * A form field with its label and a line for the error or the hint. The line keeps its height when empty,
 * so the form doesn't jump when an error appears.
 *
 * @example
 * <FormGroup label={t('auth.field.phone.label')} labelFor="phone" errorText={errors.phone?.message} required>
 *     <PhoneInput … />
 * </FormGroup>
 */
export function FormGroup({
    label,
    labelFor,
    errorText,
    helperText,
    required = false,
    className,
    children,
}: FormGroupProps): ReactElement {
    const subText = errorText ?? helperText;
    const isError = Boolean(errorText);

    return (
        <div className={classNames(styles.group, className)}>
            {label && (
                <Text as="label" weight="normal" htmlFor={labelFor}>
                    {label}
                    {required && (
                        <Text as="span" color="error" aria-hidden="true">
                            *
                        </Text>
                    )}
                </Text>
            )}

            {children}

            <Text as="p" size="sm" color={isError ? 'error' : 'muted'} className={styles.sub}>
                {subText}
            </Text>
        </div>
    );
}
